import React, { useImperativeHandle, forwardRef, useCallback } from 'react';
import Form from '@/components/pageBuilder/Elements/Form';
import { ContentWrapper, Title } from './RegisterForms.styles';
import { useFormHandler } from './useFormHandler';
import { IFormProps } from './RegisterForms.types';

type ContactDetailsRef = {
  showExistingEmailError: () => void;
};

export const ContactDetails = forwardRef<ContactDetailsRef, IFormProps>((props, ref) => {
  const { form, handleInputChange, handleInputBlur, setForm } = useFormHandler('contact');

  // Reset error on email input
  const resetTriggerError = useCallback(() => {
    const emailInput = form.find(input => input.name === 'emailAddress');
    if (emailInput) {
      emailInput.triggerError = false;
      emailInput.customErrorMessage = '';
      emailInput.status = undefined;
      setForm(prev => prev.map(input => (input.name === 'emailAddress' ? emailInput : input)));
    }
  }, [form, setForm]);

  // Show existing email error
  const showExistingEmailError = useCallback(() => {
    const emailInput = form.find(input => input.name === 'emailAddress');
    if (emailInput) {
      emailInput.customErrorMessage = 'This email is already in use. Please use another one.';
      emailInput.status = 'error';
      emailInput.triggerError = true;
      emailInput.resetTriggerError = resetTriggerError; // if this prop is still needed by Form
      setForm(prev => prev.map(input => (input.name === 'emailAddress' ? emailInput : input)));
    }
  }, [form, setForm, resetTriggerError]);

  // Expose the method via ref
  useImperativeHandle(ref, () => ({ showExistingEmailError }), [showExistingEmailError]);

  return (
    <ContentWrapper>
      <Title formTheme={props.formTheme}>Contact details</Title>
      <Form
        customInputLabelSize="14px"
        formInputs={form}
        formValidation={isValid => props.validCallback(isValid, 'contact')}
        onInputChange={handleInputChange}
        onInputBlur={handleInputBlur}
        shouldHaveFullWidthOnMobile
        formSubmit={formData => props.onSubmit?.(formData, 'contact')}
        formTheme={props.formTheme}
      />
    </ContentWrapper>
  );
});

export default ContactDetails;
